import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { env } from '@/config/env';
import { reactQueryKeys } from '@/config/react-query';
import { useCodWeb } from '@/hooks/use-codweb';
import { useExtension } from '@/hooks/use-extension';
import { fetchQueueRequest } from '@/lib/fetch-queue-request';
import { getApiHeaders } from '@/lib/getApiHeaders';
import {
  HttpError,
  queueRetryDelay,
  shouldRetryQueueRequest,
} from '@/lib/http-error';

type getExtensionPermissionsParams = { codWeb: string; extension: string };

export const ExtensionPermissionsSchema = z.object({
  rejectCalls: z.boolean(),
  autoAnswer: z.union([z.boolean(), z.number()]),
  leadIntegration: z.boolean(),
  outboundCallJournal: z.boolean().optional().default(false),
  pauseAfterMissedCall: z.union([z.boolean(), z.number()]),
  rejectCallAfter: z.union([z.boolean(), z.number()]),
  hitphoneDesktopIntegration: z.boolean(),
  showPauseAlert: z.boolean().optional().default(true),
});

export const getExtensionPermissions = async (
  params: getExtensionPermissionsParams
) => {
  const route = `${env.VITE_BACKEND_URL}/api/${params.codWeb}/extensions/${params.extension}/permissions`;
  const headers = await getApiHeaders();

  return fetchQueueRequest(route, { headers }, async (response) => {
    if (!response.ok) throw await HttpError.fromResponse(response);
    return ExtensionPermissionsSchema.parse(await response.json());
  });
};

export const useExtensionPermissions = () => {
  const codWeb = useCodWeb();
  const extension = useExtension();

  return useQuery({
    queryKey: [
      reactQueryKeys.permissions,
      String(codWeb),
      extension?.number || '',
    ],
    queryFn: () =>
      getExtensionPermissions({ codWeb, extension: extension?.number || '' }),
    enabled: Boolean(codWeb && extension?.number),
    // A failure must never fall back to default permissions; see useMode.
    retry: shouldRetryQueueRequest,
    retryDelay: queueRetryDelay,
    staleTime: Infinity,
  });
};
